import axios from "axios";
import { inject, injectable } from "inversify";
import { IPermissionsRepository } from "@domain/repositories";
import type { MePermissions, PermissionScope } from "@domain/schemas/workspace";
import { API_ENDPOINTS } from "@infrastructure/InfrastructureConstants";
import { runtimeConfig } from "@utils/runtimeConfig/runtimeConfig";
import { TYPES } from "@utils/inversifyJS/types";
import type { IAuthenticatedRequestHandlerService } from "@application/contracts/services";

/**
 * Thrown instead of issuing `GET /me/permissions` with an empty scope.
 *
 * The backend answers that request with a 400 ValidationProblem. Failing locally
 * keeps a caller bug from being reported to the user as a server error, and keeps
 * it out of the network log entirely.
 */
export class EmptyPermissionScopeError extends Error {
  constructor() {
    super("A permission scope must specify at least one of teamId or projectId.");
    this.name = "EmptyPermissionScopeError";
  }
}

// Implementation of the IPermissionsRepository interface
@injectable()
export class PermissionsRepository implements IPermissionsRepository {
  @inject(TYPES.IAuthenticatedRequestHandlerService)
  private _requestHandlerService!: IAuthenticatedRequestHandlerService;

  public async getMyPermissionsAsync(scope: PermissionScope): Promise<MePermissions> {
    const teamId = scope.teamId?.trim() || undefined;
    const projectId = scope.projectId?.trim() || undefined;

    if (teamId === undefined && projectId === undefined) {
      throw new EmptyPermissionScopeError();
    }

    const url = `${runtimeConfig.API_BASE_URL}${API_ENDPOINTS.myPermissions}`;
    const parameters = new URLSearchParams();

    if (teamId !== undefined) parameters.append("teamId", teamId);
    if (projectId !== undefined) parameters.append("projectId", projectId);

    const queryString = parameters.toString();
    const fullUrl = queryString ? `${url}?${queryString}` : url;

    return this._requestHandlerService.handleRequestAsync<MePermissions>(() => axios.get<MePermissions>(fullUrl));
  }
}
